import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { adminAPI } from '../../api/client';
import { Card, Badge, Modal, Alert, Btn, FormInput, FormSelect, Spinner, EmptyState, Avatar, fmt, fmtDate } from '../../components/UI';

const COUNTRY_OPTS = ['United States','United Kingdom','Canada','Australia','Germany','France','Netherlands','Sweden','Nigeria','Ghana','Kenya','India','Philippines','Other'].map(c=>({value:c,label:c}));
const ROLE_OPTS    = [{value:'user',label:'User'},{value:'admin',label:'Admin'}];
const STATUS_OPTS  = [
  {value:'completed',label:'✅ Available Balance (Ready to Withdraw)'},
  {value:'pending',  label:'⏳ Pending Earnings (Being Processed)'},
  {value:'paid',     label:'💰 Total Paid Out (Withdrawn)'},
];
const PLATFORM_OPTS = ['Appen','Scale AI','Remotasks','Toloka','Prolific','Outlier AI','DataAnnotation','Clickworker','iSoftStone','TELUS AI'].map(p=>({value:p,label:p}));

export default function AdminUsers() {
  const [search,       setSearch]      = useState('');
  const [page,         setPage]        = useState(1);
  const [editUser,     setEditUser]    = useState(null);
  const [earningModal, setEarningModal]= useState(null);
  const [clearPendingUser, setClearPendingUser] = useState(null);
  const [showNewUser,  setShowNewUser] = useState(false);
  const [saving,       setSaving]      = useState(false);
  const [modalError,   setModalError]  = useState('');
  const [newUserError, setNewUserError]= useState('');
  const [success,      setSuccess]     = useState('');

  const [editForm,    setEditForm]    = useState({});
  const [earnForm,    setEarnForm]    = useState({ platform_name:'Appen', amount_earned:'', user_share:'', status:'completed', notes:'' });
  const [newUserForm, setNewUserForm] = useState({ name:'', email:'', password:'', country:'United States', initial_balance:'', role:'user' });

  const fetchUsers = useCallback(() => adminAPI.users(page, search), [page, search]);
  const { data, loading, error, refetch } = useFetch(fetchUsers);
  const users      = data?.users      || [];
  const pagination = data?.pagination || {};

  function openEdit(u) {
    setEditUser(u);
    setEditForm({ name:u.name, country:u.country||'', is_active:u.is_active, role:u.role, onboarding_status: u.onboarding_status || 'pending_setup' });
    setModalError('');
  }

  function openEarning(u) {
    setEarningModal(u);
    setEarnForm({ platform_name:'Appen', amount_earned:'', user_share:'', status:'completed', notes:'' });
    setModalError('');
  }

  async function saveUser(e) {
    e.preventDefault();
    setSaving(true); setModalError('');
    try {
      await adminAPI.updateUser(editUser.id, editForm);
      setSuccess(`${editUser.name} updated successfully.`);
      setEditUser(null);
      refetch();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update user.');
    } finally { setSaving(false); }
  }

  async function markActive(user) {
    try {
      await adminAPI.updateUser(user.id, { onboarding_status: 'active' });
      setSuccess(`${user.name} marked as active. Their full dashboard is now unlocked.`);
      refetch();
    } catch (err) {
      setSuccess('');
      alert(err.response?.data?.error || 'Failed to update status.');
    }
  }

  async function addEarning(e) {
    e.preventDefault();
    if (!earnForm.amount_earned || !earnForm.user_share) { setModalError('Amounts are required.'); return; }
    setSaving(true); setModalError('');
    try {
      await adminAPI.addEarning({ user_id: earningModal.id, ...earnForm });
      setSuccess(`Earnings added for ${earningModal.name}.`);
      setEarningModal(null);
      refetch();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to add earnings.');
    } finally { setSaving(false); }
  }

  async function clearPending(target_status) {
    if (!clearPendingUser) return;
    setSaving(true); setModalError('');
    try {
      const { data } = await adminAPI.clearPending(clearPendingUser.id, target_status);
      const label = target_status === 'completed' ? 'Available Balance' : 'Total Paid Out';
      if (data.moved_count === 0) {
        setSuccess(`${clearPendingUser.name} has no pending earnings to move.`);
      } else {
        setSuccess(`Moved ${data.moved_count} pending earning(s) ($${parseFloat(data.moved_amount).toFixed(2)}) to ${label} for ${clearPendingUser.name}.`);
      }
      setClearPendingUser(null);
      refetch();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to clear pending earnings.');
    } finally { setSaving(false); }
  }

  async function createUser(e) {
    e.preventDefault();
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      setNewUserError('Name, email and password are required.'); return;
    }
    setSaving(true); setNewUserError('');
    try {
      await adminAPI.createUser(newUserForm);
      setSuccess(`Account created for ${newUserForm.name}.`);
      setShowNewUser(false);
      setNewUserForm({ name:'', email:'', password:'', country:'United States', initial_balance:'', role:'user' });
      refetch();
    } catch (err) {
      setNewUserError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to create user.');
    } finally { setSaving(false); }
  }

  const ef = k => e => setEarnForm(f=>({...f,[k]:e.target.value}));
  const uf = k => e => setEditForm(f=>({...f,[k]:e.target.type==='checkbox'?e.target.checked:e.target.value}));
  const nf = k => e => setNewUserForm(f=>({...f,[k]:e.target.value}));

  return (
    <div style={{ padding:'28px 20px', fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontFamily:"'Sora',sans-serif", fontSize:22, fontWeight:700, color:'#1a1f5e', margin:0 }}>User Management</h1>
          <p style={{ fontSize:14, color:'#9099b4', marginTop:4 }}>Manage accounts, activate setups, and add earnings.</p>
        </div>
        <Btn onClick={() => { setShowNewUser(true); setNewUserError(''); }}>+ Create User Account</Btn>
      </div>

      {success && (
        <div style={{ background:'#dcfce7', border:'1px solid #86efac', borderRadius:12, padding:'12px 16px', marginBottom:20, fontSize:14, color:'#16a34a', fontWeight:500 }}>
          ✅ {success}
        </div>
      )}

      <Card>
        <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
          <input placeholder="Search by name or email…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ flex:1, minWidth:220, padding:'10px 14px', border:'1.5px solid #e2e6f0', borderRadius:10, fontSize:14, outline:'none' }}
          />
          <span style={{ fontSize:13, color:'#9099b4', alignSelf:'center' }}>{pagination.total||0} users</span>
        </div>

        {loading ? <Spinner /> : error ? <p style={{ color:'#dc2626' }}>{error}</p>
          : users.length===0 ? <EmptyState icon="👥" message="No users found." />
          : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:780 }}>
                <thead><tr>
                  {['User','Country','Setup Status','Total Earned','Available','Account','Joined','Actions'].map(h=>(
                    <th key={h} style={{ background:'#f8f9fc', padding:'11px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#9099b4', textTransform:'uppercase', letterSpacing:'.4px', borderBottom:'1px solid #e2e6f0', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {users.map(u=>(
                    <tr key={u.id} style={{ borderBottom:'1px solid #f0f2f8' }}>
                      <td style={{ padding:'13px 12px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <Avatar name={u.name} size={32} />
                          <div>
                            <div style={{ fontWeight:600, color:'#1a1f5e', fontSize:14 }}>{u.name}</div>
                            <div style={{ fontSize:12, color:'#9099b4' }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:'13px 12px', fontSize:13, color:'#636b89' }}>{u.country||'—'}</td>

                      {/* Setup status */}
                      <td style={{ padding:'13px 12px' }}>
                        {u.onboarding_status === 'pending_setup' ? (
                          <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'#fef9c3', color:'#b45309' }}>⏳ Pending Setup</span>
                        ) : (
                          <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'#dcfce7', color:'#16a34a' }}>✅ Active</span>
                        )}
                      </td>

                      <td style={{ padding:'13px 12px' }}><strong style={{ color:'#1a1f5e' }}>{fmt(u.total_earned)}</strong></td>
                      <td style={{ padding:'13px 12px' }}><strong style={{ color:'#16a34a' }}>{fmt(u.available_balance)}</strong></td>
                      <td style={{ padding:'13px 12px' }}><Badge status={u.is_active?'active':'suspended'} /></td>
                      <td style={{ padding:'13px 12px', fontSize:12, color:'#9099b4', whiteSpace:'nowrap' }}>{fmtDate(u.created_at)}</td>
                      <td style={{ padding:'13px 12px' }}>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          {/* Mark active button — only shown for pending users */}
                          {u.onboarding_status === 'pending_setup' && (
                            <button onClick={() => markActive(u)} style={{ padding:'5px 10px', background:'#dcfce7', color:'#16a34a', border:'1px solid #86efac', borderRadius:7, fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                              ✅ Mark Active
                            </button>
                          )}
                          <Btn variant="ghost" style={{ fontSize:12, padding:'5px 10px' }} onClick={()=>openEdit(u)}>✏️ Edit</Btn>
                          <Btn variant="ghost" style={{ fontSize:12, padding:'5px 10px', color:'#16a34a' }} onClick={()=>openEarning(u)}>+ Earnings</Btn>
                          <Btn variant="ghost" style={{ fontSize:12, padding:'5px 10px', color:'#7c3aed' }} onClick={()=>{ setClearPendingUser(u); setModalError(''); }}>⏳ Clear Pending</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
        {pagination.total > pagination.limit && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:20, paddingTop:16 }}>
            <button style={{ padding:'7px 16px', border:'1.5px solid #e2e6f0', borderRadius:8, background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#636b89' }} disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</button>
            <span style={{ fontSize:13, color:'#636b89' }}>Page {page} of {Math.ceil(pagination.total/pagination.limit)}</span>
            <button style={{ padding:'7px 16px', border:'1.5px solid #e2e6f0', borderRadius:8, background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#636b89' }} disabled={page*pagination.limit>=pagination.total} onClick={()=>setPage(p=>p+1)}>Next →</button>
          </div>
        )}
      </Card>

      {/* CREATE USER */}
      <Modal open={showNewUser} onClose={()=>setShowNewUser(false)} title="Create User Account">
        <Alert type="error" message={newUserError} />
        <div style={{ background:'#eef1ff', borderRadius:10, padding:'11px 14px', marginBottom:16, fontSize:13, color:'#4338ca' }}>
          ℹ️ Account will be created as <strong>Pending Setup</strong>. Click "Mark Active" once you've helped them set up their platform account.
        </div>
        <form onSubmit={createUser}>
          <FormInput label="Full Name" placeholder="e.g. James Mitchell" value={newUserForm.name} onChange={nf('name')} required />
          <FormInput label="Email Address" type="email" placeholder="user@example.com" value={newUserForm.email} onChange={nf('email')} required />
          <FormInput label="Password" type="password" placeholder="Min 8 chars, 1 uppercase, 1 number" value={newUserForm.password} onChange={nf('password')} required />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FormSelect label="Country" options={COUNTRY_OPTS} value={newUserForm.country} onChange={nf('country')} />
            <FormSelect label="Role" options={ROLE_OPTS} value={newUserForm.role} onChange={nf('role')} />
          </div>
          <FormInput label="Initial Balance ($) — optional" type="number" min="0" step="0.01" placeholder="0.00" value={newUserForm.initial_balance} onChange={nf('initial_balance')} />
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Btn variant="outline" type="button" onClick={()=>setShowNewUser(false)}>Cancel</Btn>
            <Btn loading={saving} type="submit" variant="success">Create Account</Btn>
          </div>
        </form>
      </Modal>

      {/* EDIT USER */}
      <Modal open={!!editUser} onClose={()=>setEditUser(null)} title={`Edit — ${editUser?.name}`}>
        <Alert type="error" message={modalError} />
        <form onSubmit={saveUser}>
          <FormInput label="Full Name" value={editForm.name||''} onChange={uf('name')} required />
          <FormSelect label="Country" options={COUNTRY_OPTS} value={editForm.country||''} onChange={uf('country')} />
          <FormSelect label="Role" options={ROLE_OPTS} value={editForm.role||'user'} onChange={uf('role')} />

          {/* Onboarding status toggle */}
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#2e3456', marginBottom:8 }}>Setup Status</label>
            <div style={{ display:'flex', gap:10 }}>
              {['pending_setup','active'].map(status => (
                <button key={status} type="button" onClick={() => setEditForm(f=>({...f, onboarding_status:status}))}
                  style={{ flex:1, padding:'9px', borderRadius:8, border:`2px solid ${editForm.onboarding_status===status ? (status==='active'?'#16a34a':'#f4a61d') : '#e2e6f0'}`, background: editForm.onboarding_status===status ? (status==='active'?'#dcfce7':'#fef9c3') : '#fff', color: editForm.onboarding_status===status ? (status==='active'?'#16a34a':'#b45309') : '#9099b4', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  {status === 'active' ? '✅ Active' : '⏳ Pending Setup'}
                </button>
              ))}
            </div>
          </div>

          {/* Account active toggle */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <label style={{ fontSize:13, fontWeight:600, color:'#2e3456' }}>Account Active</label>
            <div onClick={()=>setEditForm(f=>({...f,is_active:!f.is_active}))} style={{ width:44, height:24, borderRadius:12, background:editForm.is_active?'#4f6ef7':'#e2e6f0', position:'relative', cursor:'pointer', transition:'background .2s' }}>
              <div style={{ width:18, height:18, borderRadius:'50%', background:'#fff', position:'absolute', top:3, left:editForm.is_active?23:3, transition:'left .2s', boxShadow:'0 1px 3px rgba(0,0,0,.2)' }} />
            </div>
            <span style={{ fontSize:13, color:editForm.is_active?'#16a34a':'#dc2626', fontWeight:600 }}>{editForm.is_active?'Active':'Suspended'}</span>
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Btn variant="outline" type="button" onClick={()=>setEditUser(null)}>Cancel</Btn>
            <Btn loading={saving} type="submit">Save Changes</Btn>
          </div>
        </form>
      </Modal>

      {/* CLEAR PENDING EARNINGS */}
      <Modal open={!!clearPendingUser} onClose={()=>setClearPendingUser(null)} title={`Clear Pending Earnings — ${clearPendingUser?.name}`}>
        <Alert type="error" message={modalError} />
        <div style={{ background:'#eef1ff', borderRadius:10, padding:'12px 14px', marginBottom:16, fontSize:13, color:'#4338ca', lineHeight:1.6 }}>
          Move <strong>all of {clearPendingUser?.name}'s</strong> pending earnings into one of the dashboard balances below. This affects every record currently marked <em>Pending</em>.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <Btn variant="success" onClick={()=>clearPending('completed')} loading={saving}>
            ✅ Move all pending → Available Balance
          </Btn>
          <Btn onClick={()=>clearPending('paid')} loading={saving} style={{ background:'#16a34a' }}>
            💰 Move all pending → Total Paid Out
          </Btn>
          <Btn variant="outline" type="button" onClick={()=>setClearPendingUser(null)}>Cancel</Btn>
        </div>
      </Modal>

      {/* ADD EARNINGS */}
      <Modal open={!!earningModal} onClose={()=>setEarningModal(null)} title={`Add Earnings — ${earningModal?.name}`}>
        <Alert type="error" message={modalError} />
        <div style={{ background:'#eef1ff', borderRadius:10, padding:'11px 14px', marginBottom:16, fontSize:12, color:'#4338ca', lineHeight:1.55 }}>
          ℹ️ <strong>How this affects the user's dashboard:</strong> The <strong>Current Earnings</strong> amount is added to the dashboard balance you choose below. <strong>Total Earnings</strong> on their dashboard is the sum of all three.
        </div>
        <form onSubmit={addEarning}>
          <FormSelect label="Platform" options={PLATFORM_OPTS} value={earnForm.platform_name} onChange={ef('platform_name')} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FormInput label="Total Earned ($)"     type="number" min="0" step="0.01" placeholder="0.00" value={earnForm.amount_earned} onChange={ef('amount_earned')} required />
            <FormInput label="Current Earnings ($)" type="number" min="0" step="0.01" placeholder="0.00" value={earnForm.user_share} onChange={ef('user_share')} required />
          </div>
          <FormSelect label="Add to which balance?" options={STATUS_OPTS} value={earnForm.status} onChange={ef('status')} />
          <FormInput label="Notes (optional)" placeholder="Any notes" value={earnForm.notes} onChange={ef('notes')} />
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Btn variant="outline" type="button" onClick={()=>setEarningModal(null)}>Cancel</Btn>
            <Btn loading={saving} type="submit" variant="success">Add Earnings</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
